var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import _ from "lodash";
import { AbstractParseTreeVisitor } from "antlr4ts/tree/AbstractParseTreeVisitor";
import { FilterExpressionParser } from "../antlr/FilterExpressionParser.js";
import { tryGetString, tryGetBoolean, tryGetInteger, tryGetVersion } from "./FilterExpressionParser.utils.js";
import { isVersion, versionCompareTo, stringCompareTo, numberCompareTo, booleanCompareTo } from "../../common/utils/common.utils.js";
class FilterEvaluator extends AbstractParseTreeVisitor {
  constructor(variants) {
    super();
    __publicField(this, "_variants");
    /// <summary>
    /// Gets or sets a value indicating whether logical operators (AND, OR) will be short-circuited.
    /// </summary>
    __publicField(this, "ShortCircuitEvaluation", false);
    this._variants = {};
    _.each(Object.keys(variants), (key) => {
      this._variants[key.toLowerCase()] = variants[key];
    });
  }
  defaultResult() {
    return false;
  }
  visitFilterIn(context) {
    const variant = context.VARIANT();
    const rhs = context.literalList().literal();
    return this.evaluateCondition(variant, context._op, rhs);
  }
  visitFilterBinary(context) {
    const variant = context.VARIANT();
    const rhs = context.literal();
    return this.evaluateCondition(variant, context._op, [rhs]);
  }
  visitFilterBetween(context) {
    const variant = context.VARIANT();
    const op = context.BETWEEN().symbol;
    const rhs = context.literal();
    return this.evaluateCondition(variant, op, rhs);
  }
  visitFilterSet(context) {
    let isMatch = true;
    _.each(context.filterExpr(), (filterClause) => {
      isMatch = isMatch && filterClause.accept(this);
      if (!isMatch && this.ShortCircuitEvaluation) {
        return false;
      }
    });
    return isMatch;
  }
  visitFilterGroup(context) {
    return context.filterExpr().accept(this);
  }
  visitFilterExprBase(context) {
    return context.filterCondition().accept(this);
  }
  visitFilterConjunction(context) {
    const lhs = context.filterExpr(0).accept(this);
    if (!lhs && this.ShortCircuitEvaluation) {
      return false;
    }
    const rhs = context.filterExpr(1).accept(this);
    return lhs && rhs;
  }
  visitFilterDisjunction(context) {
    const lhs = context.filterExpr(0).accept(this);
    if (lhs && this.ShortCircuitEvaluation) {
      return true;
    }
    const rhs = context.filterExpr(1).accept(this);
    return lhs || rhs;
  }
  evaluateCondition(variant, op, rhs) {
    const variantName = variant.symbol.text;
    const lhs = this._variants[(variantName == null ? void 0 : variantName.toLowerCase()) || ""];
    if (lhs == null || lhs === void 0) {
      throw new Error(`Unspecified variant "${variantName}".`);
    }
    if (typeof lhs === "string" && isVersion(lhs)) {
      const lhsVersion = lhs;
      return this.evaluateConditionInner(
        (rhsVersion) => versionCompareTo(lhsVersion, rhsVersion),
        op,
        _.map(rhs, (r) => FilterEvaluator.getVersion(r))
      );
    } else if (typeof lhs === "string") {
      const lhsString = lhs;
      return this.evaluateConditionInner(
        (rhsString) => stringCompareTo(lhsString, rhsString),
        op,
        _.map(rhs, (r) => FilterEvaluator.getString(r))
      );
    }
    switch (typeof lhs) {
      case "boolean":
        const lhsBoolean = lhs;
        return this.evaluateConditionInner(
          (rhsBoolean) => booleanCompareTo(lhsBoolean, rhsBoolean),
          op,
          rhs.map((r) => FilterEvaluator.getBoolean(r))
        );
      case "number":
        const lhsInteger = +lhs;
        return this.evaluateConditionInner(
          (rhsInteger) => numberCompareTo(lhsInteger, rhsInteger),
          op,
          rhs.map((r) => FilterEvaluator.getInteger(r))
        );
    }
    throw new Error(`variantName "${variantName}" has unsupported type "${typeof lhs}".`);
  }
  static getString(context) {
    const { success, value } = tryGetString(context);
    if (success) {
      return value;
    }
    throw new Error(`Expected a STRING token instead of: "${context.text}".`);
  }
  static getBoolean(context) {
    const { success, value } = tryGetBoolean(context);
    if (success) {
      return value;
    }
    throw new Error(`Expected a BOOLEAN token instead of: "${context.text}".`);
  }
  static getInteger(context) {
    const { success, value } = tryGetInteger(context);
    if (success) {
      return value;
    }
    throw new Error(`Expected a INTEGER token instead of: "${context.text}".`);
  }
  static getVersion(context) {
    const { success, value } = tryGetVersion(context);
    if (success) {
      return value;
    }
    throw new Error(`Expected a VERSION token instead of: "${context.text}".`);
  }
  evaluateConditionInner(lhsCompare, op, rhs) {
    switch (op.type) {
      case FilterExpressionParser.IN:
      case FilterExpressionParser.EQ:
        return rhs.some((value) => lhsCompare(value) === 0);
      case FilterExpressionParser.NE:
      case FilterExpressionParser.NOTIN:
        return rhs.every((value) => lhsCompare(value) !== 0);
      case FilterExpressionParser.BETWEEN:
        const lowerBound = rhs[0];
        const upperBound = rhs[1];
        return lhsCompare(lowerBound) >= 0 && lhsCompare(upperBound) <= 0;
    }
    const result = lhsCompare(rhs[0]);
    switch (op.type) {
      case FilterExpressionParser.LE:
        return result <= 0;
      case FilterExpressionParser.LT:
        return result < 0;
      case FilterExpressionParser.GE:
        return result >= 0;
      case FilterExpressionParser.GT:
        return result > 0;
      default:
        throw new Error(`Unknown filter operator "${op.text}".`);
    }
  }
}
export {
  FilterEvaluator
};
//# sourceMappingURL=FilterEvaluator.js.map
